<script>
    import { onMount } from 'svelte';
    import {
        masterAuth,
        fetchMasterStores,
        createMasterStore,
        updateMasterStore,
        deleteMasterStore,
        fetchMasterAiSettings,
        saveMasterAiSettings,
        testMasterAiConnection,
        fetchMasterMpSettings,
        saveMasterMpSettings,
        testMasterMpSettings,
        getMasterSupport,
        saveMasterSupport,
        getApiBase
    } from '../services/api';
    import Icon from '../components/icons/Icon.svelte';

    // Subcomponentes modulares do Portal Master SaaS
    import MasterAuthCard from '../components/master/MasterAuthCard.svelte';
    import MasterMetricsGrid from '../components/master/MasterMetricsGrid.svelte';
    import MasterStoresTab from '../components/master/MasterStoresTab.svelte';
    import MasterAiTab from '../components/master/MasterAiTab.svelte';
    import MasterPaymentsTab from '../components/master/MasterPaymentsTab.svelte';
    import MasterSupportTab from '../components/master/MasterSupportTab.svelte';
    import CreateStoreModal from '../components/master/CreateStoreModal.svelte';
    import StoreSuccessModal from '../components/master/StoreSuccessModal.svelte';
    import DeleteStoreModal from '../components/master/DeleteStoreModal.svelte';

    export let onBack = () => {};

    // Navegação de Abas do Portal ('stores' | 'ai' | 'payments' | 'support')
    let activeTab = 'stores';

    // Autenticação Master
    let masterKey = '';
    let isMasterAuth = false;
    let authError = '';
    let isAuthenticating = false;

    // Feedback Toast
    let toastMessage = '';
    let toastType = 'success';
    let toastTimer = null;

    function showToast(msg, type = 'success') {
        toastMessage = msg;
        toastType = type;
        if (toastTimer) clearTimeout(toastTimer);
        toastTimer = setTimeout(() => { toastMessage = ''; }, 3500);
    }

    // Dados das Lojas & Métricas
    let isLoading = false;
    let stores = [];
    let metrics = {
        totalStores: 0,
        activeStores: 0,
        blockedStores: 0,
        totalMRR: 0.00
    };
    let searchQuery = '';
    let statusFilter = 'all';

    // Modais
    let showCreateModal = false;
    let isCreatingStore = false;
    let createError = '';
    let newStoreName = '';
    let newStoreEmail = '';
    let newStorePassword = '';
    let newStoreSlug = '';
    let newStorePhone = '';
    let newStorePrice = '97.00';
    let newStoreNiche = 'hamburgueria';

    let showSuccessModal = false;
    let createdStoreData = null;

    let showDeleteModal = false;
    let deleteTargetStore = null;
    let isDeletingStore = false;

    // Configurações de IA
    let aiSettings = {
        activeProvider: 'gemini',
        hasGeminiKey: false,
        hasOpenaiKey: false,
        geminiKeyMasked: '',
        openaiKeyMasked: ''
    };
    let inputGeminiKey = '';
    let inputOpenaiKey = '';
    let inputActiveProvider = 'gemini';
    let isSavingAi = false;
    let isTestingGemini = false;
    let isTestingOpenai = false;
    let geminiTestResult = null;
    let openaiTestResult = null;

    // Configurações de Mercado Pago
    let mpSettings = {
        hasToken: false,
        hasClientSecret: false,
        mpAccessTokenMasked: '',
        mpClientId: '',
        mpClientSecretMasked: '',
        webhookUrl: '',
        redirectUri: ''
    };
    let inputMpAccessToken = '';
    let inputMpClientId = '';
    let inputMpClientSecret = '';
    let isSavingMp = false;
    let isTestingMp = false;
    let mpTestResult = null;

    // Configurações de Suporte
    let supportSettings = {
        whatsapp: '',
        phone: '',
        email: '',
        message: ''
    };
    let inputSupportWhatsapp = '';
    let inputSupportPhone = '';
    let inputSupportEmail = '';
    let inputSupportMessage = '';
    let isSavingSupport = false;

    onMount(async () => {
        try {
            const savedKey = sessionStorage.getItem('matafome_master_key');
            if (savedKey) {
                masterKey = savedKey;
                await handleMasterAuth();
            }
        } catch (_) {}
    });

    // 1. Autenticação na Sessão Master
    async function handleMasterAuth() {
        if (!masterKey.trim()) {
            authError = 'Informe a chave mestra.';
            return;
        }

        isAuthenticating = true;
        authError = '';
        try {
            const res = await masterAuth(masterKey.trim());
            if (res && res.success) {
                isMasterAuth = true;
                try {
                    sessionStorage.setItem('matafome_master_key', masterKey.trim());
                } catch (_) {}
                showToast('Autenticado com sucesso no Painel Master!', 'success');
                await loadStores();
            } else {
                authError = 'Chave mestra inválida.';
            }
        } catch (err) {
            authError = err.message || 'Falha na autenticação Master.';
        } finally {
            isAuthenticating = false;
        }
    }

    function handleLogoutMaster() {
        isMasterAuth = false;
        masterKey = '';
        try {
            sessionStorage.removeItem('matafome_master_key');
        } catch (_) {}
        showToast('Sessão Master encerrada.', 'info');
    }

    // 2. Carregar Lojas & Métricas
    async function loadStores() {
        isLoading = true;
        try {
            const res = await fetchMasterStores(masterKey);
            if (res && res.success) {
                stores = res.stores || [];
                metrics = res.metrics || {
                    totalStores: stores.length,
                    activeStores: stores.filter(s => s.subscription_status === 'active').length,
                    blockedStores: stores.filter(s => s.subscription_status === 'blocked').length,
                    totalMRR: stores.filter(s => s.subscription_status === 'active').reduce((acc, s) => acc + (s.monthly_price || 0), 0)
                };
            }
        } catch (err) {
            showToast('Erro ao carregar lojas: ' + err.message, 'error');
        } finally {
            isLoading = false;
        }
    }

    // 3. Cadastrar Restaurante
    async function handleCreateStore() {
        if (!newStoreName.trim() || !newStoreEmail.trim() || !newStorePassword.trim()) {
            createError = 'Nome, e-mail e senha são obrigatórios.';
            return;
        }

        isCreatingStore = true;
        createError = '';
        try {
            const res = await createMasterStore({
                name: newStoreName.trim(),
                email: newStoreEmail.trim(),
                password: newStorePassword.trim(),
                slug: newStoreSlug.trim(),
                phone: newStorePhone.trim(),
                monthlyPrice: parseFloat(newStorePrice || '97.00'),
                businessProfile: newStoreNiche
            }, masterKey);

            if (res && res.store) {
                createdStoreData = {
                    ...res.store,
                    password: newStorePassword.trim()
                };
                showCreateModal = false;
                showSuccessModal = true;

                // Limpa form
                newStoreName = '';
                newStoreEmail = '';
                newStorePassword = '';
                newStoreSlug = '';
                newStorePhone = '';
                newStorePrice = '97.00';
                newStoreNiche = 'hamburgueria';

                await loadStores();
                showToast(`Restaurante "${res.store.name}" cadastrado com sucesso!`, 'success');
            }
        } catch (err) {
            createError = err.message || 'Erro ao cadastrar restaurante.';
        } finally {
            isCreatingStore = false;
        }
    }

    // 4. Bloquear / Desbloquear Loja
    async function handleToggleBlockStore(store) {
        const isCurrentlyActive = store.subscription_status === 'active';
        const nextStatus = isCurrentlyActive ? 'blocked' : 'active';
        const actionLabel = isCurrentlyActive ? 'bloquear o acesso de' : 'desbloquear';

        if (!confirm(`Deseja ${actionLabel} "${store.name}"?`)) return;

        try {
            await updateMasterStore(store.id, {
                status: nextStatus === 'active' ? 'active' : 'suspended',
                subscriptionStatus: nextStatus
            }, masterKey);

            showToast(`Status de "${store.name}" alterado para ${nextStatus === 'active' ? 'Ativo' : 'Bloqueado'}!`, 'success');
            await loadStores();
        } catch (err) {
            showToast('Erro ao alterar status: ' + err.message, 'error');
        }
    }

    // 5. Salvar Preço de Mensalidade
    async function handleSavePrice(store, newPriceStr) {
        const price = parseFloat(newPriceStr);
        if (isNaN(price) || price < 0) {
            showToast('Valor de mensalidade inválido.', 'error');
            return;
        }

        try {
            await updateMasterStore(store.id, { monthlyPrice: price }, masterKey);
            showToast(`Mensalidade de "${store.name}" atualizada para R$ ${price.toFixed(2)}/mês!`, 'success');
            await loadStores();
        } catch (err) {
            showToast('Erro ao atualizar mensalidade: ' + err.message, 'error');
        }
    }

    // 6. Excluir Loja
    function openDeleteModal(store) {
        deleteTargetStore = store;
        showDeleteModal = true;
    }

    async function handleConfirmDelete() {
        if (!deleteTargetStore) return;
        isDeletingStore = true;
        try {
            await deleteMasterStore(deleteTargetStore.id, masterKey);
            showToast(`Restaurante "${deleteTargetStore.name}" excluído permanentemente.`, 'info');
            showDeleteModal = false;
            deleteTargetStore = null;
            await loadStores();
        } catch (err) {
            showToast('Erro ao excluir loja: ' + err.message, 'error');
        } finally {
            isDeletingStore = false;
        }
    }

    // 7. Copiar Texto / Boas-Vindas
    function copyText(text, msg = 'Copiado para a área de transferência!') {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text);
            showToast(msg, 'success');
        }
    }

    function copyWhatsAppWelcome(store) {
        if (!store) return;
        const origin = getApiBase() || (typeof window !== 'undefined' ? window.location.origin : 'https://app.matafome.com');
        const cardapioUrl = `${origin}/cardapio/${store.slug}`;
        const text = `🍔 *Seu Acesso ao MataFome está Pronto!*\n\n` +
            `🏪 *Restaurante:* ${store.name}\n` +
            `📧 *E-mail:* ${store.email}\n` +
            `🔑 *Senha:* ${store.password || '(Sua senha definida)'}\n` +
            `🎫 *Chave de Acesso:* ${store.access_key}\n` +
            `💲 *Mensalidade:* R$ ${parseFloat(store.monthly_price || 97).toFixed(2)}/mês\n\n` +
            `🔗 *Seu Cardápio Digital:* ${cardapioUrl}\n\n` +
            `Baixe o app ou acesse pelo computador para começar a gerenciar seus pedidos e ativar o WhatsApp!`;

        copyText(text, 'Mensagem de boas-vindas copiada para o WhatsApp!');
    }

    // 8. Configurações de IA
    async function loadAiSettings() {
        try {
            const data = await fetchMasterAiSettings(masterKey);
            if (data) {
                aiSettings = { ...aiSettings, ...data };
                inputActiveProvider = data.activeProvider || 'gemini';
            }
        } catch (err) {
            showToast('Erro ao carregar IA: ' + err.message, 'error');
        }
    }

    async function handleSaveAiSettings() {
        isSavingAi = true;
        try {
            const payload = { activeProvider: inputActiveProvider };
            if (inputGeminiKey) payload.geminiApiKey = inputGeminiKey.trim();
            if (inputOpenaiKey) payload.openaiApiKey = inputOpenaiKey.trim();
            await saveMasterAiSettings(payload, masterKey);
            showToast('Configurações globais de IA salvas com sucesso!', 'success');
            inputGeminiKey = '';
            inputOpenaiKey = '';
            await loadAiSettings();
        } catch (err) {
            showToast('Erro ao salvar IA: ' + err.message, 'error');
        } finally {
            isSavingAi = false;
        }
    }

    async function handleTestAi(provider, apiKey) {
        if (provider === 'gemini') {
            isTestingGemini = true;
            geminiTestResult = null;
        } else {
            isTestingOpenai = true;
            openaiTestResult = null;
        }
        try {
            const res = await testMasterAiConnection(provider, apiKey ? apiKey.trim() : undefined, masterKey);
            if (provider === 'gemini') {
                geminiTestResult = { success: true, message: `Conexão bem-sucedida com Gemini (${res.latencyMs || 0}ms).` };
            } else {
                openaiTestResult = { success: true, message: `Conexão bem-sucedida com OpenAI (${res.latencyMs || 0}ms).` };
            }
            showToast(`Conexão com ${provider === 'gemini' ? 'Gemini' : 'OpenAI'} aprovada!`, 'success');
        } catch (err) {
            const errMsg = err.message || 'Falha ao testar conexão.';
            if (provider === 'gemini') {
                geminiTestResult = { success: false, message: errMsg };
            } else {
                openaiTestResult = { success: false, message: errMsg };
            }
            showToast(`Erro ao testar ${provider}: ${errMsg}`, 'error');
        } finally {
            if (provider === 'gemini') isTestingGemini = false;
            else isTestingOpenai = false;
        }
    }

    // 9. Configurações de Mercado Pago
    async function loadMpSettings() {
        try {
            const data = await fetchMasterMpSettings(masterKey);
            if (data) {
                mpSettings = { ...mpSettings, ...data };
                inputMpClientId = data.mpClientId || '';
            }
        } catch (err) {
            showToast('Erro ao carregar Mercado Pago: ' + err.message, 'error');
        }
    }

    async function handleSaveMpSettings() {
        isSavingMp = true;
        try {
            const payload = { mpClientId: inputMpClientId.trim() };
            if (inputMpAccessToken) payload.mpAccessToken = inputMpAccessToken.trim();
            if (inputMpClientSecret) payload.mpClientSecret = inputMpClientSecret.trim();
            await saveMasterMpSettings(payload, masterKey);
            showToast('Credenciais do Mercado Pago salvas!', 'success');
            inputMpAccessToken = '';
            inputMpClientSecret = '';
            await loadMpSettings();
        } catch (err) {
            showToast('Erro ao salvar MP: ' + err.message, 'error');
        } finally {
            isSavingMp = false;
        }
    }

    async function handleTestMpSettings() {
        isTestingMp = true;
        mpTestResult = null;
        try {
            const payload = {};
            if (inputMpAccessToken) payload.mpAccessToken = inputMpAccessToken.trim();
            const res = await testMasterMpSettings(payload, masterKey);
            mpTestResult = { success: true, message: `Conexão Mercado Pago validada com sucesso! Conta: ${res?.collector?.email || 'OK'}` };
            showToast('Credenciais Mercado Pago validadas!', 'success');
        } catch (err) {
            mpTestResult = { success: false, message: err.message || 'Falha ao validar credenciais.' };
            showToast('Falha no Mercado Pago: ' + err.message, 'error');
        } finally {
            isTestingMp = false;
        }
    }

    // 10. Configurações de Suporte
    async function loadSupportSettings() {
        try {
            const data = await getMasterSupport(masterKey);
            if (data) {
                supportSettings = { ...supportSettings, ...data };
                inputSupportWhatsapp = data.whatsapp || '';
                inputSupportPhone = data.phone || '';
                inputSupportEmail = data.email || '';
                inputSupportMessage = data.message || '';
            }
        } catch (err) {
            showToast('Erro ao carregar suporte: ' + err.message, 'error');
        }
    }

    async function handleSaveSupportSettings() {
        isSavingSupport = true;
        try {
            const payload = {
                whatsapp: inputSupportWhatsapp.trim(),
                phone: inputSupportPhone.trim(),
                email: inputSupportEmail.trim(),
                message: inputSupportMessage.trim()
            };
            await saveMasterSupport(payload, masterKey);
            showToast('Canais de suporte oficiais salvos!', 'success');
            await loadSupportSettings();
        } catch (err) {
            showToast('Erro ao salvar suporte: ' + err.message, 'error');
        } finally {
            isSavingSupport = false;
        }
    }

    function handleKeydown(e) {
        if (e.key === 'Escape') {
            if (showCreateModal && !isCreatingStore) showCreateModal = false;
            if (showSuccessModal) showSuccessModal = false;
            if (showDeleteModal && !isDeletingStore) showDeleteModal = false;
        }
    }
</script>

<svelte:window on:keydown={handleKeydown} />

<div class="master-container {!isMasterAuth ? 'is-auth-screen' : ''}">
    {#if toastMessage}
        <div class="toast-banner {toastType}">
            <span>{toastMessage}</span>
        </div>
    {/if}

    {#if !isMasterAuth}
        <!-- TELA DE AUTENTICAÇÃO MASTER -->
        <MasterAuthCard
            bind:masterKey={masterKey}
            isAuthenticating={isAuthenticating}
            authError={authError}
            onSubmit={handleMasterAuth}
            onBack={onBack}
        />
    {:else}
        <!-- PAINEL PRINCIPAL MASTER SAAS -->
        <div class="master-content">
            <!-- Header Executivo -->
            <header class="master-header">
                <div class="header-left">
                    <div class="header-badge-row">
                        <span class="pulse-dot"></span>
                        <span class="portal-badge">PORTAL MASTER SAAS</span>
                        <span class="version-tag">MataFome v1.0.2</span>
                    </div>
                    <h1>Gestão de Clientes & Faturamento</h1>
                    <p class="header-subtitle">Cockpit executivo consolidado para administração de restaurantes, planos, MRR e integrações.</p>
                </div>

                <div class="header-actions">
                    <button type="button" class="btn-action-refresh" on:click={loadStores} title="Recarregar dados">
                        <Icon name="refresh" size={14} />
                        <span>Atualizar</span>
                    </button>

                    <button type="button" class="btn-action-create" on:click={() => showCreateModal = true}>
                        <Icon name="plus" size={15} />
                        <span>Novo Restaurante</span>
                    </button>

                    <button type="button" class="btn-action-logout" on:click={handleLogoutMaster} title="Encerrar sessão">
                        Sair do Master
                    </button>
                </div>
            </header>

            <!-- Navegação de Abas Executivas -->
            <nav class="master-nav">
                <button
                    type="button"
                    class="nav-tab {activeTab === 'stores' ? 'active' : ''}"
                    on:click={() => activeTab = 'stores'}
                >
                    <Icon name="building" size={16} />
                    <span>Estabelecimentos & MRR</span>
                    <span class="tab-badge">{metrics.totalStores}</span>
                </button>

                <button
                    type="button"
                    class="nav-tab {activeTab === 'ai' ? 'active' : ''}"
                    on:click={() => { activeTab = 'ai'; loadAiSettings(); }}
                >
                    <Icon name="bot" size={16} />
                    <span>Configurações Globais de IA</span>
                    <span class="status-indicator {aiSettings.hasGeminiKey || aiSettings.hasOpenaiKey ? 'active' : 'pending'}">
                        ● {aiSettings.hasGeminiKey || aiSettings.hasOpenaiKey ? 'Ativo' : 'Pendente'}
                    </span>
                </button>

                <button
                    type="button"
                    class="nav-tab {activeTab === 'payments' ? 'active' : ''}"
                    on:click={() => { activeTab = 'payments'; loadMpSettings(); }}
                >
                    <Icon name="credit-card" size={16} />
                    <span>Mercado Pago & Gateway</span>
                    <span class="status-indicator {mpSettings.hasToken ? 'active' : 'pending'}">
                        ● {mpSettings.hasToken ? 'Ativo' : 'Pendente'}
                    </span>
                </button>

                <button
                    type="button"
                    class="nav-tab {activeTab === 'support' ? 'active' : ''}"
                    on:click={() => { activeTab = 'support'; loadSupportSettings(); }}
                >
                    <Icon name="message-circle" size={16} />
                    <span>Canais de Suporte Oficial</span>
                    <span class="status-indicator {supportSettings.whatsapp ? 'active' : 'pending'}">
                        ● {supportSettings.whatsapp ? 'Ativo' : 'Pendente'}
                    </span>
                </button>
            </nav>

            <!-- Conteúdo da Aba Ativa -->
            <main class="master-tab-content">
                {#if activeTab === 'stores'}
                    <!-- Cards de Métricas SaaS exibidos exclusivamente na aba de Lojas/MRR -->
                    <MasterMetricsGrid metrics={metrics} />

                    <MasterStoresTab
                        stores={stores}
                        isLoading={isLoading}
                        bind:searchQuery={searchQuery}
                        bind:statusFilter={statusFilter}
                        onOpenCreateModal={() => showCreateModal = true}
                        onToggleBlockStore={handleToggleBlockStore}
                        onOpenDeleteModal={openDeleteModal}
                        onSavePrice={handleSavePrice}
                        onCopyWelcome={copyWhatsAppWelcome}
                        onCopyText={copyText}
                    />
                {:else if activeTab === 'ai'}
                    <MasterAiTab
                        aiSettings={aiSettings}
                        bind:inputGeminiKey={inputGeminiKey}
                        bind:inputOpenaiKey={inputOpenaiKey}
                        bind:inputActiveProvider={inputActiveProvider}
                        isSavingAi={isSavingAi}
                        isTestingGemini={isTestingGemini}
                        isTestingOpenai={isTestingOpenai}
                        geminiTestResult={geminiTestResult}
                        openaiTestResult={openaiTestResult}
                        onSaveAiSettings={handleSaveAiSettings}
                        onTestAi={handleTestAi}
                    />
                {:else if activeTab === 'payments'}
                    <MasterPaymentsTab
                        mpSettings={mpSettings}
                        bind:inputMpAccessToken={inputMpAccessToken}
                        bind:inputMpClientId={inputMpClientId}
                        bind:inputMpClientSecret={inputMpClientSecret}
                        isSavingMp={isSavingMp}
                        isTestingMp={isTestingMp}
                        mpTestResult={mpTestResult}
                        onSaveMpSettings={handleSaveMpSettings}
                        onTestMpSettings={handleTestMpSettings}
                    />
                {:else if activeTab === 'support'}
                    <MasterSupportTab
                        supportSettings={supportSettings}
                        bind:inputSupportWhatsapp={inputSupportWhatsapp}
                        bind:inputSupportPhone={inputSupportPhone}
                        bind:inputSupportEmail={inputSupportEmail}
                        bind:inputSupportMessage={inputSupportMessage}
                        isSavingSupport={isSavingSupport}
                        onSaveSupportSettings={handleSaveSupportSettings}
                    />
                {/if}
            </main>
        </div>
    {/if}

    <!-- Modais -->
    <CreateStoreModal
        isOpen={showCreateModal}
        bind:name={newStoreName}
        bind:email={newStoreEmail}
        bind:password={newStorePassword}
        bind:slug={newStoreSlug}
        bind:phone={newStorePhone}
        bind:monthlyPrice={newStorePrice}
        bind:businessProfile={newStoreNiche}
        isCreating={isCreatingStore}
        createError={createError}
        onSubmit={handleCreateStore}
        onClose={() => showCreateModal = false}
    />

    <StoreSuccessModal
        isOpen={showSuccessModal}
        store={createdStoreData}
        onCopyWelcome={copyWhatsAppWelcome}
        onCopyText={copyText}
        onClose={() => showSuccessModal = false}
    />

    <DeleteStoreModal
        isOpen={showDeleteModal}
        store={deleteTargetStore}
        isDeleting={isDeletingStore}
        onConfirmDelete={handleConfirmDelete}
        onClose={() => showDeleteModal = false}
    />
</div>

<style>
    .master-container {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        min-height: 100%;
        box-sizing: border-box;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        background: #F8FAFC;
        color: #0F172A;
    }

    .master-container.is-auth-screen {
        background: radial-gradient(circle at 50% 30%, #1A1F2B 0%, #0A0D14 70%);
        color: #F3F4F6;
        padding: 0;
    }

    .master-content {
        padding: 24px 32px 48px;
        max-width: 1300px;
        margin: 0 auto;
        width: 100%;
        box-sizing: border-box;
    }

    .toast-banner {
        position: fixed;
        top: 20px;
        right: 20px;
        padding: 12px 20px;
        border-radius: 8px;
        font-size: 0.86rem;
        font-weight: 600;
        z-index: 10000;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        animation: slideIn 0.25s ease;
    }

    .toast-banner.success {
        background: #16a34a;
        color: #fff;
    }

    .toast-banner.error {
        background: #dc2626;
        color: #fff;
    }

    .toast-banner.info {
        background: #2563eb;
        color: #fff;
    }

    @keyframes slideIn {
        from { transform: translateY(-10px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
    }

    .master-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: 24px;
        gap: 16px;
        flex-wrap: wrap;
        padding-bottom: 20px;
        border-bottom: 1px solid #E2E8F0;
    }

    .header-badge-row {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 6px;
    }

    .pulse-dot {
        display: none;
    }

    .portal-badge {
        font-size: 11px;
        font-weight: 800;
        color: #FF6A00;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        background: rgba(255, 106, 0, 0.08);
        border: 1px solid rgba(255, 106, 0, 0.2);
        padding: 3px 8px;
        border-radius: 6px;
    }

    .version-tag {
        font-size: 11px;
        background: #F1F5F9;
        color: #475569;
        border: 1px solid #E2E8F0;
        padding: 3px 9px;
        border-radius: 9999px;
        font-weight: 600;
    }

    .master-header h1 {
        font-size: 22px;
        font-weight: 800;
        color: #020617;
        margin: 0 0 4px 0;
        letter-spacing: -0.4px;
    }

    .header-subtitle {
        font-size: 13px;
        color: #64748B;
        margin: 0;
        max-width: 680px;
    }

    .header-actions {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .btn-action-refresh {
        background: #FFFFFF;
        border: 1px solid #CBD5E1;
        color: #475569;
        padding: 8px 14px;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        transition: all 0.2s;
    }

    .btn-action-refresh:hover {
        background: #F8FAFC;
        color: #0F172A;
        border-color: #94A3B8;
    }

    .btn-action-create {
        background: #FF6A00;
        border: 1px solid #EA580C;
        color: #FFFFFF;
        padding: 8px 18px;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        box-shadow: 0 1px 3px rgba(255, 106, 0, 0.2);
        transition: all 0.2s;
    }

    .btn-action-create:hover {
        background: #EA580C;
        box-shadow: 0 3px 8px rgba(255, 106, 0, 0.3);
        transform: translateY(-1px);
    }

    .btn-action-logout {
        background: #FFFFFF;
        border: 1px solid #FCA5A5;
        color: #DC2626;
        padding: 8px 14px;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
    }

    .btn-action-logout:hover {
        background: #FEF2F2;
    }

    .master-nav {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        padding: 6px;
        border-radius: 12px;
        margin-bottom: 24px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
        overflow-x: auto;
        max-width: 100%;
    }

    .nav-tab {
        background: transparent;
        border: 1px solid transparent;
        color: #475569;
        padding: 8px 14px;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        white-space: nowrap;
        transition: all 0.15s ease;
    }

    .nav-tab:hover {
        background: #F8FAFC;
        color: #0F172A;
    }

    .nav-tab.active {
        background: #FF6A00;
        color: #FFFFFF;
        border-color: #EA580C;
        box-shadow: 0 2px 6px rgba(255, 106, 0, 0.25);
    }

    .tab-badge {
        font-size: 11px;
        font-weight: 800;
        background: #F1F5F9;
        color: #475569;
        padding: 2px 6px;
        border-radius: 6px;
    }

    .nav-tab.active .tab-badge {
        background: rgba(255, 255, 255, 0.25);
        color: #FFFFFF;
    }

    .status-indicator {
        font-size: 11px;
        font-weight: 700;
    }

    .status-indicator.active {
        color: #16A34A;
    }

    .nav-tab.active .status-indicator.active {
        color: #FFFFFF;
    }

    .status-indicator.pending {
        color: #94A3B8;
    }

    .nav-tab.active .status-indicator.pending {
        color: rgba(255, 255, 255, 0.7);
    }

    .master-tab-content {
        min-height: 450px;
    }
</style>
